/**
 * Deterministic per-(specialty, city) content variation so Google sees each
 * /specialty/$specialty/$city page as distinct instead of templated boilerplate.
 *
 * All functions are pure and synchronous so they can run in route head() and
 * server-side rendering without extra fetches.
 */

export type CityFacts = {
  short: string;
  state: string;
  tier: "mega" | "major" | "large";
  systems: string[];
  climateNote: string;
  lat: number;
  lng: number;
  metroPopM: number;
};

export const CITY_FACTS: Record<string, CityFacts> = {
  "Arlington, TX": {
    short: "Arlington", state: "TX", tier: "large",
    systems: ["Texas Health Arlington Memorial", "Medical City Arlington"],
    climateNote: "hot, humid summers that drive demand for dermatology and allergy care",
    lat: 32.7357, lng: -97.1081, metroPopM: 0.4,
  },
  "Dallas, TX": {
    short: "Dallas", state: "TX", tier: "mega",
    systems: ["Baylor Scott & White", "UT Southwestern", "Texas Health Resources"],
    climateNote: "long allergy seasons and high UV exposure year-round",
    lat: 32.7767, lng: -96.7970, metroPopM: 7.9,
  },
  "Houston, TX": {
    short: "Houston", state: "TX", tier: "mega",
    systems: ["Houston Methodist", "Memorial Hermann", "MD Anderson", "Texas Children's"],
    climateNote: "humidity-driven asthma and dermatology demand",
    lat: 29.7604, lng: -95.3698, metroPopM: 7.3,
  },
  "Austin, TX": {
    short: "Austin", state: "TX", tier: "major",
    systems: ["Ascension Seton", "St. David's HealthCare", "Dell Medical"],
    climateNote: "high cedar pollen counts and an active, outdoor lifestyle",
    lat: 30.2672, lng: -97.7431, metroPopM: 2.4,
  },
  "Phoenix, AZ": {
    short: "Phoenix", state: "AZ", tier: "major",
    systems: ["Banner Health", "HonorHealth", "Mayo Clinic Arizona"],
    climateNote: "extreme summer heat and very high UV index, increasing dermatology and hydration-related care",
    lat: 33.4484, lng: -112.0740, metroPopM: 5.0,
  },
  "Los Angeles, CA": {
    short: "Los Angeles", state: "CA", tier: "mega",
    systems: ["Cedars-Sinai", "Kaiser Permanente", "UCLA Health", "Keck Medicine of USC"],
    climateNote: "wildfire smoke seasons that drive respiratory and allergy visits",
    lat: 34.0522, lng: -118.2437, metroPopM: 13.2,
  },
  "San Diego, CA": {
    short: "San Diego", state: "CA", tier: "major",
    systems: ["Scripps Health", "Sharp HealthCare", "UC San Diego Health"],
    climateNote: "mild coastal climate and a strong sports-medicine and outdoor-injury demand",
    lat: 32.7157, lng: -117.1611, metroPopM: 3.3,
  },
  "Chicago, IL": {
    short: "Chicago", state: "IL", tier: "mega",
    systems: ["Northwestern Medicine", "Rush University Medical", "Advocate Health"],
    climateNote: "cold winters that drive seasonal-affective and respiratory-illness visits",
    lat: 41.8781, lng: -87.6298, metroPopM: 9.4,
  },
  "Miami, FL": {
    short: "Miami", state: "FL", tier: "major",
    systems: ["Baptist Health South Florida", "Jackson Health System", "University of Miami Health"],
    climateNote: "year-round UV exposure and a large bilingual patient population",
    lat: 25.7617, lng: -80.1918, metroPopM: 6.1,
  },
  "New York, NY": {
    short: "New York", state: "NY", tier: "mega",
    systems: ["NYU Langone", "Mount Sinai", "NewYork-Presbyterian", "Northwell Health"],
    climateNote: "dense urban living, long commutes, and broad insurance-network variation borough to borough",
    lat: 40.7128, lng: -74.0060, metroPopM: 19.5,
  },
  "Seattle, WA": {
    short: "Seattle", state: "WA", tier: "major",
    systems: ["UW Medicine", "Swedish Medical Center", "Virginia Mason Franciscan"],
    climateNote: "long overcast winters that drive vitamin-D and seasonal-affective care",
    lat: 47.6062, lng: -122.3321, metroPopM: 4.0,
  },
  "Denver, CO": {
    short: "Denver", state: "CO", tier: "major",
    systems: ["UCHealth", "HealthONE", "SCL Health", "Denver Health"],
    climateNote: "high altitude and very active outdoor population, with strong sports-medicine demand",
    lat: 39.7392, lng: -104.9903, metroPopM: 3.0,
  },
};

/**
 * Truly nearby cities for each metro. Used for the "nearby cities" chips so
 * we don't show e.g. Denver or Miami next to Arlington, TX. These need NOT
 * be landing pages; chips link to /search?location=... when they aren't.
 */
export const LOCAL_NEARBY: Record<string, string[]> = {
  "Arlington, TX": ["Fort Worth, TX", "Dallas, TX", "Grand Prairie, TX", "Irving, TX", "Mansfield, TX", "Euless, TX", "Bedford, TX", "Hurst, TX", "North Richland Hills, TX", "Grapevine, TX"],
  "Dallas, TX": ["Fort Worth, TX", "Arlington, TX", "Plano, TX", "Irving, TX", "Garland, TX", "Frisco, TX", "Mesquite, TX", "Richardson, TX", "Carrollton, TX", "McKinney, TX"],
  "Houston, TX": ["Sugar Land, TX", "The Woodlands, TX", "Pasadena, TX", "Pearland, TX", "Katy, TX", "Spring, TX", "Cypress, TX", "Conroe, TX", "Baytown, TX", "League City, TX"],
  "Austin, TX": ["Round Rock, TX", "Cedar Park, TX", "Pflugerville, TX", "Georgetown, TX", "Leander, TX", "Lakeway, TX", "Buda, TX", "Kyle, TX", "San Marcos, TX", "Hutto, TX"],
  "Phoenix, AZ": ["Mesa, AZ", "Scottsdale, AZ", "Chandler, AZ", "Glendale, AZ", "Gilbert, AZ", "Tempe, AZ", "Peoria, AZ", "Surprise, AZ", "Goodyear, AZ", "Avondale, AZ"],
  "Los Angeles, CA": ["Long Beach, CA", "Glendale, CA", "Pasadena, CA", "Santa Monica, CA", "Burbank, CA", "Inglewood, CA", "Torrance, CA", "Anaheim, CA", "Irvine, CA", "Santa Ana, CA"],
  "San Diego, CA": ["Chula Vista, CA", "Oceanside, CA", "Escondido, CA", "Carlsbad, CA", "El Cajon, CA", "Vista, CA", "San Marcos, CA", "Encinitas, CA", "La Mesa, CA", "Poway, CA"],
  "Chicago, IL": ["Evanston, IL", "Naperville, IL", "Aurora, IL", "Schaumburg, IL", "Oak Park, IL", "Cicero, IL", "Skokie, IL", "Arlington Heights, IL", "Joliet, IL", "Elgin, IL"],
  "Miami, FL": ["Hialeah, FL", "Miami Beach, FL", "Coral Gables, FL", "Doral, FL", "Aventura, FL", "Homestead, FL", "Kendall, FL", "Fort Lauderdale, FL", "Hollywood, FL", "Pembroke Pines, FL"],
  "New York, NY": ["Brooklyn, NY", "Queens, NY", "Bronx, NY", "Staten Island, NY", "Jersey City, NJ", "Newark, NJ", "Yonkers, NY", "Hoboken, NJ", "White Plains, NY", "New Rochelle, NY"],
  "Seattle, WA": ["Bellevue, WA", "Redmond, WA", "Kirkland, WA", "Renton, WA", "Tacoma, WA", "Everett, WA", "Bothell, WA", "Federal Way, WA", "Issaquah, WA", "Kent, WA"],
  "Denver, CO": ["Aurora, CO", "Lakewood, CO", "Thornton, CO", "Arvada, CO", "Westminster, CO", "Centennial, CO", "Englewood, CO", "Boulder, CO", "Littleton, CO", "Wheat Ridge, CO"],
};

/* ============================================================
   Grammar helpers — handle specialty names like
   "Allergists / Immunologists", "Dietitians / Nutritionists"
   ============================================================ */

const NON_COUNTABLE_SPECIALTIES = new Set<string>([
  "Internal Medicine",
  "Pain Management",
  "Physical Therapy",
  "Primary Care",
  "Sports Medicine",
  "Urgent Care",
  "Women's Health",
  "ENT (Ear, Nose & Throat)",
]);

function specialtyParts(s: string): string[] {
  return s.split("/").map((p) => p.trim()).filter(Boolean);
}

/** Plural display, e.g. "Allergists & Immunologists". */
export function specialtyPlural(s: string): string {
  return specialtyParts(s).join(" & ");
}

function singularPart(p: string): string {
  if (NON_COUNTABLE_SPECIALTIES.has(p)) return p;
  // crude depluralisation that handles our list correctly
  if (p.endsWith("ies")) return p.slice(0, -3) + "y";
  if (p.endsWith("ss")) return p;
  if (p.endsWith("s")) return p.slice(0, -1);
  return p;
}

/** Singular display, e.g. "Allergist or Immunologist". */
export function specialtySingular(s: string): string {
  if (NON_COUNTABLE_SPECIALTIES.has(s)) return s;
  return specialtyParts(s).map(singularPart).join(" or ");
}

function articleFor(word: string): "an" | "a" {
  const first = word.trim()[0]?.toLowerCase() ?? "";
  return ["a", "e", "i", "o", "u"].includes(first) ? "an" : "a";
}

/** e.g. "an allergist or immunologist", "a cardiologist". */
export function specialtyWithArticle(s: string): string {
  const sing = specialtySingular(s).toLowerCase();
  return `${articleFor(sing)} ${sing}`;
}

/** Lowercase plural for inline copy, e.g. "allergists & immunologists". */
export function specialtyPluralLower(s: string): string {
  return specialtyPlural(s).toLowerCase();
}

export function pairHash(specialty: string, city: string): number {
  let h = 2166136261;
  const s = `${specialty.toLowerCase()}|${city.toLowerCase()}`;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return Math.abs(h);
}

export function variantIndex(specialty: string, city: string, n: number): number {
  return pairHash(specialty, city) % Math.max(1, n);
}

/** Intro paragraph — varies by hash AND by provider count. */
export function buildIntroParagraph(opts: {
  specialty: string;
  city: string;
  providerCount: number;
}): string {
  const facts = CITY_FACTS[opts.city];
  const cityShort = facts?.short ?? opts.city.split(",")[0];
  const state = facts?.state ?? "";
  const plural = specialtyPluralLower(opts.specialty);
  const withArticle = specialtyWithArticle(opts.specialty);
  const count = opts.providerCount;

  // Zero-provider intros — honest, no "wide range" claims.
  if (count === 0) {
    const zeroVariants = [
      `No ${plural} are listed in ${cityShort} yet. Below are nearby cities and broader results across ${state || "the state"} so you can still find ${withArticle} who fits your needs.`,
      `We don't have ${plural} listed in ${cityShort} right now. You can broaden your search to ${state || "the state"} or pick a nearby city below — most ${state || "regional"} insurance networks extend across the metro.`,
      `${cityShort} doesn't have ${plural} listed on Heallexa yet. Try a nearby city or the full ${state || "state"} results below to find ${withArticle} that accepts your insurance.`,
    ];
    return zeroVariants[variantIndex(opts.specialty, opts.city, zeroVariants.length)];
  }

  const idx = variantIndex(opts.specialty, opts.city, 4);
  const countPhrase = count >= 10 ? `${count}+ verified ${plural}` : `${count} ${plural}`;
  const systemMention = facts?.systems?.length
    ? `Many practice at ${facts.systems.slice(0, 2).join(" or ")}.`
    : "";
  const climateMention = facts?.climateNote
    ? `${cityShort} has ${facts.climateNote}, which shapes how local ${plural} structure their practice.`
    : "";
  const tierAdj = facts?.tier === "mega" ? "deep" : facts?.tier === "major" ? "strong" : "growing";

  const variants = [
    `Heallexa lists ${countPhrase} in ${cityShort}, ${state}. ${systemMention} Compare credentials, listed insurance, and patient reviews side by side — free, no signup. ${climateMention}`,
    `Finding the right ${withArticle.replace(/^an? /, "")} in ${cityShort} means weighing insurance coverage, distance, and bedside manner. ${climateMention} The metro has a ${tierAdj} bench of ${plural} — ${countPhrase} are listed below. ${systemMention}`,
    `Looking for ${withArticle} in ${cityShort}? Most patients narrow the list by insurance first, then by location and reviews. ${countPhrase} appear below. ${systemMention} ${climateMention}`,
    `Choosing ${withArticle} in ${cityShort}, ${state} is easier when you can see credentials, listed plans, and patient ratings in one place. Heallexa shows ${countPhrase} side by side. ${systemMention} ${climateMention}`,
  ];

  return variants[idx].replace(/\s+/g, " ").trim();
}

export function buildFaqs(opts: {
  specialty: string;
  city: string;
}): Array<{ q: string; a: string }> {
  const facts = CITY_FACTS[opts.city];
  const cityShort = facts?.short ?? opts.city.split(",")[0];
  const plural = specialtyPluralLower(opts.specialty);
  const withArticle = specialtyWithArticle(opts.specialty);
  const singularLower = specialtySingular(opts.specialty).toLowerCase();
  const state = facts?.state ?? "";
  const idx = variantIndex(opts.specialty, opts.city, 3);

  const rotations: Array<Array<{ q: string; a: string }>> = [
    [
      {
        q: `How much does a visit to ${withArticle} cost in ${cityShort}?`,
        a: `In-network ${plural} in ${cityShort} typically run $${facts?.tier === "mega" ? "150–$400" : "120–$300"} for an initial visit before insurance. Out-of-network rates can be 3–5× higher. Use the insurance filter above to see ${cityShort} ${plural} who list your specific plan, and confirm acceptance directly with the office before booking.`,
      },
      {
        q: `Are ${cityShort} ${plural} accepting new patients?`,
        a: `Most ${cityShort} listings indicate new-patient status on the profile. Wait times vary by specialty and insurance; calling the office directly is the fastest way to confirm same-week availability.`,
      },
      {
        q: `Which insurance plans do ${cityShort} ${plural} list?`,
        a: `Major plans in ${state} include Blue Cross Blue Shield, Aetna, UnitedHealthcare, Cigna, Humana, and Medicare. Listings show plans the provider has indicated they accept — always confirm in-network status directly with the office before your appointment.`,
      },
      {
        q: `Do I need a referral to see ${withArticle} in ${cityShort}?`,
        a: `Most PPO plans in ${state} allow direct booking. HMO and many Medicare Advantage plans require a primary-care referral first. Confirm with your insurer or the office before booking.`,
      },
    ],
    [
      {
        q: `What should I ask ${withArticle} in ${cityShort} before booking?`,
        a: `Confirm three things: (1) they currently accept your specific insurance plan and network tier, (2) the office location and parking, and (3) typical wait times for new-patient visits. ${facts?.systems?.length ? `Providers tied to ${facts.systems[0]} often have shorter waits if you're already in-system.` : ""}`,
      },
      {
        q: `Is telehealth available with ${cityShort} ${plural}?`,
        a: `Many ${cityShort} ${plural} offer telehealth visits, especially for follow-ups, prescription refills, and consultations. Look for the telehealth badge on each profile.`,
      },
      {
        q: `How are these ${cityShort} ${plural} verified?`,
        a: `Every listing on Heallexa is matched against NPI records and state license databases. Credentials, board certifications, and active license status are surfaced on each profile.`,
      },
      {
        q: `Is Heallexa free for patients in ${cityShort}?`,
        a: `Yes. Searching, comparing, and contacting ${cityShort} ${plural} on Heallexa is 100% free for patients. We don't charge booking fees or sell your search data.`,
      },
    ],
    [
      {
        q: `How do I find the highest-rated ${singularLower} in ${cityShort}?`,
        a: `Sort the list above by rating. Heallexa surfaces verified patient reviews alongside credentials so you can see whether a provider's strengths match what you actually need.`,
      },
      {
        q: `What if no ${cityShort} ${plural} accept my insurance?`,
        a: `Try the surrounding metro — ${state} insurance networks often extend across multiple cities. The nearby-cities links below open the same specialty in adjacent markets. Always confirm coverage directly with the provider.`,
      },
      {
        q: `Are walk-in visits available with ${cityShort} ${plural}?`,
        a: `Walk-ins are uncommon for most ${plural}. The "available today" filter shows providers with open same-day slots.`,
      },
      {
        q: `How often is this ${cityShort} list updated?`,
        a: `Heallexa refreshes provider data from public NPI records and license databases on a rolling basis. Listings that fall out of the active registry are removed automatically.`,
      },
    ],
  ];

  return rotations[idx];
}

/** Pick `n` nearby cities — prefers LOCAL_NEARBY (true metro neighbours),
 * then falls back to other landing-page cities ordered by geo-distance. */
export function pickNearbyCities(opts: {
  specialty: string;
  currentCity: string;
  allCities: readonly string[];
  count?: number;
}): string[] {
  const count = opts.count ?? 8;
  const local = LOCAL_NEARBY[opts.currentCity] ?? [];
  if (local.length >= count) return local.slice(0, count);

  const facts = CITY_FACTS[opts.currentCity];
  const others = opts.allCities.filter(
    (c) => c !== opts.currentCity && !local.includes(c),
  );
  if (!facts) return [...local, ...others].slice(0, count);

  const scored = others.map((c) => {
    const f = CITY_FACTS[c];
    if (!f) return { c, score: 9999 };
    const sameState = f.state === facts.state ? -1000 : 0;
    const dx = f.lat - facts.lat;
    const dy = f.lng - facts.lng;
    return { c, score: sameState + dx * dx + dy * dy };
  });
  scored.sort((a, b) => a.score - b.score);
  return [...local, ...scored.map((s) => s.c)].slice(0, count);
}

/** Is this city a real landing page (in our CITIES list)? */
export function isLandingCity(
  city: string,
  allCities: readonly string[],
): boolean {
  return allCities.includes(city);
}

/* ============================================================
   Centralized meta builder — single source of truth for
   title / description / H1 across the specialty+city pages.
   Guarantees uniqueness per (specialty, city) and grammar
   correctness for singular/plural specialty names.
   ============================================================ */

export type SpecialtyCityMeta = {
  title: string;
  description: string;
  h1: string;
  /** Stable signature combining the unique parts of title+desc+h1. */
  uniquenessKey: string;
  /** True when the page has at least one useful content block
   * (providers, nearby chips, or FAQs). */
  hasUsefulContent: boolean;
};

export function buildSpecialtyCityMeta(opts: {
  specialty: string;
  city: string;
  providerCount: number;
  nearbyCount?: number;
  faqCount?: number;
}): SpecialtyCityMeta {
  const facts = CITY_FACTS[opts.city];
  const cityShort = facts?.short ?? opts.city.split(",")[0];
  const stateShort = facts?.state ?? (opts.city.split(",")[1] ?? "").trim();
  const count = opts.providerCount;
  const specialtyDisplay = specialtyPlural(opts.specialty);
  const pluralLower = specialtyPluralLower(opts.specialty);
  const withArticle = specialtyWithArticle(opts.specialty);

  const cityState = stateShort ? `${cityShort}, ${stateShort}` : cityShort;

  const fullTitle = count > 0
    ? `${count} Best ${specialtyDisplay} in ${cityState} (2026) | Heallexa`
    : `${specialtyDisplay} in ${cityState} | Heallexa`;
  const shortTitle = count > 0
    ? `Best ${specialtyDisplay} in ${cityState} (2026)`
    : `${specialtyDisplay} in ${cityState}`;
  // Keep under 60 chars for SEO; fall back to the short form when needed.
  const title = fullTitle.length <= 60 ? fullTitle : shortTitle;

  const description = count > 0
    ? `Find top-rated ${pluralLower} in ${opts.city}. Compare ${count}+ providers, read reviews, and see listed insurance plans (BCBS, Aetna, UnitedHealthcare, Medicare). Free on Heallexa.`
    : `Looking for ${withArticle} in ${opts.city}? No providers are listed here yet — browse nearby cities and broader ${stateShort || "state"} results on Heallexa. Free, no signup.`;

  const h1 = count > 0
    ? `Best ${specialtyDisplay} in ${cityState}`
    : `${specialtyDisplay} in ${cityState}`;

  const hasUsefulContent =
    count > 0 ||
    (opts.nearbyCount ?? 0) > 0 ||
    (opts.faqCount ?? 0) > 0;

  return {
    title,
    description,
    h1,
    uniquenessKey: `${opts.specialty}::${opts.city}::${count > 0 ? "has" : "zero"}`,
    hasUsefulContent,
  };
}

